import { ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { getProgression } from "../../lib/quiz-module";
import type { ChildProfile } from "../../types";
import "./child-stats.scss";

type StatsData = {
  currentStep: number;
  gradeGroup: string;
  totalResponses: number;
  accuracy: number;
  uniqueWords: number;
  stepProgress: { seen: number; total: number };
  daysActive: number;
  lastActiveAt: string | null;
  recentActivity: { date: string; count: number }[];
  transitions: {
    fromStep: number;
    toStep: number;
    reason: string | null;
    transitionedAt: string;
  }[];
};

type Props = {
  child: ChildProfile;
  onBack: () => void;
};

export default function ChildStats({ child, onBack }: Props) {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    fetch(`/api/children/${child.id}/stats`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load stats");
        return res.json();
      })
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [child.id]);

  const progression = getProgression(child.gradeGroup);
  const stepCfg = progression.find((p) => p.step === child.currentStep);
  const cefrLabel = stepCfg ? stepCfg.cefrLevel : "—";
  const tierLabel = stepCfg
    ? stepCfg.tier === "receptive"
      ? "קליטה"
      : "הפקה"
    : "";

  if (loading) {
    return (
      <main id="main-content" className="stats-page" dir="rtl">
        <div className="stats-header">
          <button className="stats-back" onClick={onBack} aria-label="חזרה">
            <ArrowRight className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
          </button>
          <h2 className="stats-name">{child.name} · אוצר מילים</h2>
        </div>
      </main>
    );
  }

  if (!stats) {
    return (
      <main id="main-content" className="stats-page" dir="rtl">
        <div className="stats-header">
          <button className="stats-back" onClick={onBack} aria-label="חזרה">
            <ArrowRight className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
          </button>
          <h2 className="stats-name">{child.name} · אוצר מילים</h2>
        </div>
        <p className="stats-empty">לא הצלחנו לטעון את הנתונים</p>
      </main>
    );
  }

  const progressPct =
    stats.stepProgress.total > 0
      ? Math.round((stats.stepProgress.seen / stats.stepProgress.total) * 100)
      : 0;

  // Build last 7 days for activity chart
  const today = new Date();
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (6 - i));
    return d.toISOString().slice(0, 10);
  });
  const activityMap = new Map(stats.recentActivity.map((r) => [r.date, r.count]));
  const maxActivity = Math.max(1, ...last7.map((d) => activityMap.get(d) ?? 0));

  const dayNames = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

  const lastActive = stats.lastActiveAt
    ? formatRelativeDate(stats.lastActiveAt) + ` (${formatDate(stats.lastActiveAt)})`
    : "עדיין לא";

  return (
    <main id="main-content" className="stats-page" dir="rtl">
      <div className="stats-content">
      <div className="stats-header">
        <button className="stats-back" onClick={onBack} aria-label="חזרה">
          <ArrowRight className="brand-icon"  aria-hidden="true" strokeWidth={2.4} />
        </button>
        <h2 className="stats-name">{child.name} · אוצר מילים</h2>
      </div>

      {/* Level badge */}
      <div className="stats-level">
        <span className="stats-level-step">שלב באוצר מילים {stats.currentStep}</span>
        <span className="stats-level-cefr">
          {cefrLabel} · {tierLabel}
        </span>
      </div>

      {/* KPI cards */}
      <div className="stats-kpis">
        <div className="kpi-card">
          <span className="kpi-value">{stats.accuracy}%</span>
          <span className="kpi-label">דיוק</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{stats.totalResponses}</span>
          <span className="kpi-label">שאלות</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{stats.uniqueWords}</span>
          <span className="kpi-label">מילים</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{stats.daysActive}</span>
          <span className="kpi-label">ימים פעילים</span>
        </div>
      </div>

      {/* Step progress */}
      <div className="stats-section">
        <div className="section-title">התקדמות בשלב הנוכחי</div>
        <div className="progress-row">
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="progress-label">
            {stats.stepProgress.seen} / {stats.stepProgress.total}
          </span>
        </div>
      </div>

      {/* Activity chart (last 7 days) */}
      <div className="stats-section">
        <div className="section-row">
          <span className="section-title">פעילות אחרונה</span>
          <span className="section-subtitle">פעילות אחרונה: {lastActive}</span>
        </div>
        <div className="activity-chart">
          {last7.map((date) => {
            const count = activityMap.get(date) ?? 0;
            const height = count > 0 ? Math.max(8, (count / maxActivity) * 100) : 4;
            const dayIdx = new Date(date).getDay();
            return (
              <div className="activity-day" key={date}>
                {count > 0 && <span className="activity-count">{count}</span>}
                <div className="activity-bar-container">
                  <div
                    className={`activity-bar ${count > 0 ? "active" : ""}`}
                    style={{ height: `${height}%` }}
                  />
                </div>
                <span className="activity-label">{dayNames[dayIdx]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step transitions timeline */}
      {stats.transitions.length > 0 && (
        <div className="stats-section">
          <div className="section-title">היסטוריית שלבים</div>
          <div className="timeline">
            {stats.transitions.map((t, i) => (
              <div className="timeline-item" key={i}>
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <span className="timeline-step">
                    שלב {t.fromStep} → {t.toStep}
                  </span>
                  <span className="timeline-meta">
                    {formatDate(t.transitionedAt)}
                    {t.reason === "auto" ? " · אוטומטי" : " · ידני"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>
    </main>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
  });
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "היום";
  if (diffDays === 1) return "אתמול";
  if (diffDays < 7) return `לפני ${diffDays} ימים`;
  return formatDate(iso);
}
